/**
 * F 327 — THE ENVIRONMENT-SECRET KEY PATH.
 *
 * The credential store already exists and already refuses to derive a key
 * itself ("The store never derives a key itself: the CALLER supplies one").
 * FCr's only caller-supplied key path was `keyFromPassphrase`. This module is
 * the second one, and it exists because the dispatch requires a key derived
 * from an ENVIRONMENT SECRET rather than a user passphrase.
 *
 * WHY BOTH, AND NOT INSTEAD OF
 * ----------------------------
 * A passphrase says "who is allowed to read this". An environment secret says
 * "what is this machine allowed to do". They answer different questions, and
 * one resolver cannot answer both:
 *
 *   - a USER secret (a person's provider key) must be passphrase-gated, or any
 *     code in the process reads it;
 *   - a MACHINE secret (the pipeline's own Groq/Gemini/NVIDIA keys) must be
 *     inherited by every app that mounts FCr, which a per-user passphrase
 *     cannot be.
 *
 * So this module adds a resolver; it does not replace `keyFromPassphrase`.
 *
 * THE WEAKNESS, NAMED RATHER THAN DISCOVERED LATER
 * ------------------------------------------------
 * An environment secret is AMBIENT. Every code path in the process — and
 * every third-party app plugin mounted on FCr — can read the same variable
 * and therefore re-derive the same key. An environment secret provides
 * *separation at rest*, not isolation between callers in the process. A
 * credential protected only by an env secret is exactly as readable as the
 * environment to anything that can already run there.
 *
 * Gemini's verdict on this design called the same weakness: "Any code
 * executing in the same process environment (including compromised or
 * malicious third-party app plugins mounted on FCr) can read the environment
 * variable and derive the key." That is why `ENV_KEY_SCOPE` is an explicit
 * tag on the resolver: a consumer can tell an env-backed credential from a
 * passphrase-backed one and refuse the weaker one for user secrets.
 *
 * NOTHING HERE LOGS THE SECRET
 * ----------------------------
 * The error paths name the VARIABLE, never its value. There is no code path in
 * this file that can put the secret into a string that gets logged, returned
 * for display, or embedded in a Setting.
 */

import { deriveKeyWithKdf, type KdfId } from '../../crypto';
import type { KeyResolver, Principal } from './credentialStore';

/**
 * THE KEY CONTRACT, which the first draft of this file got wrong and the tests
 * caught: a `KeyResolver` must return **base64 of exactly 32 bytes** (AES-256).
 * `CredentialStore.encryptWithKeyFor` does `b64ToBuf(key)` and imports it as an
 * AES-GCM key, so passing a raw secret string fails inside `atob` with
 * `InvalidCharacterError` — a failure that surfaces as "the cipher broke",
 * not as "the resolver returned the wrong shape".
 *
 * So the env secret is DERIVED, not passed through. That is also what the
 * dispatch means by "a key derived from an environment secret".
 */
const DERIVED_KEY_BYTES = 32;

/**
 * How a resolver obtained its key material.
 *
 * `environment` is the weaker model (ambient, process-wide); `passphrase` is
 * the stronger one (gated on a human). Consumers that care SHOULD check this
 * before using a credential for a user secret.
 */
export type EnvKeyScope = 'environment';

export interface EnvironmentKeyOptions {
  /**
   * The environment variable holding the secret.
   *
   * Named, never guessed: a store that scanned the environment for something
   * that "looked like a key" would be reading whatever the host happened to
   * export.
   */
  variable: string;
  /** How to read the variable. Overridable so tests never touch a real env. */
  read?: (variable: string) => string | undefined;
  /** Reject a secret shorter than this. Default 8; 0 disables the check. */
  minLength?: number;
}

/** Thrown when the env secret is absent or too short to be a real secret. */
export class EnvSecretUnavailableError extends Error {
  /** The VARIABLE NAME. Never the value. */
  readonly variable: string;
  constructor(variable: string, reason: string) {
    super(`[credentials] env secret ${variable} is unusable: ${reason}`);
    this.name = 'EnvSecretUnavailableError';
    this.variable = variable;
  }
}

/** Read from a supplied map first, then `process.env`. */
function defaultRead(variable: string): string | undefined {
  const fromEnv =
    typeof process !== 'undefined' && process.env ? process.env[variable] : undefined;
  return fromEnv;
}

/**
 * Read an environment secret, or throw naming only the variable.
 *
 * Throwing rather than returning '' matters: an empty secret would silently
 * produce a key that encrypts and cannot be decrypted, and the failure would
 * surface as "this credential decrypts to nothing" much later.
 */
export function readEnvironmentSecret(opts: EnvironmentKeyOptions): string {
  const read = opts.read ?? defaultRead;
  const raw = read(opts.variable);
  if (raw === undefined || raw === null || raw === '') {
    throw new EnvSecretUnavailableError(opts.variable, 'not set');
  }
  const secret = String(raw);
  const min = opts.minLength ?? 8;
  if (min > 0 && secret.length < min) {
    // The LENGTH is safe to report. The value never is.
    throw new EnvSecretUnavailableError(
      opts.variable,
      `shorter than the minimum of ${min} characters`,
    );
  }
  return secret;
}

/**
 * Derive the 32-byte base64 key an env secret stands for.
 *
 * THE SALT IS DETERMINISTIC AND THAT IS REQUIRED, NOT A SHORTCUT: a credential
 * sealed today has to be openable tomorrow, and a per-call random salt would
 * make every read fail. The salt is bound to the VARIABLE NAME, not the value,
 * so two providers' secrets do not share one — and it is public by
 * construction, which is fine: a KDF salt is not a secret.
 *
 * PBKDF2 rather than Argon2id because the Argon2 WASM path is explicitly a
 * best-effort fallback in this substrate (`kdf.ts` returns null and falls back),
 * and a key derivation that can be absent in a test environment would make the
 * store unreadable for reasons that have nothing to do with the secret.
 */
export async function deriveEnvironmentKey(opts: EnvironmentKeyOptions): Promise<string> {
  const secret = readEnvironmentSecret(opts);
  // The salt MUST be base64, not an arbitrary string: `deriveKeyPBKDF2` does
  // `base64ToArrayBuffer(salt)` before hashing. A dotted label like
  // "fcr.envkey.v1.X" throws InvalidCharacterError inside `atob`, and the
  // stack points at the KDF rather than at the caller — the second time this
  // file got blamed for the cipher's shape.
  const salt = btoa(`fcr.envkey.v1.${opts.variable}`);
  const key = await deriveKeyWithKdf(secret, salt, 'pbkdf2' as KdfId);
  // A short key is a silent security downgrade, so check rather than hope.
  const bytes = Math.floor((key.replace(/=+$/, '').length * 3) / 4);
  if (bytes < DERIVED_KEY_BYTES) {
    // The DERIVED KEY LENGTH is safe to report. The secret never is.
    throw new EnvSecretUnavailableError(
      opts.variable,
      `derived only ${bytes} key bytes, need ${DERIVED_KEY_BYTES}`,
    );
  }
  return key;
}

/**
 * A `KeyResolver` backed by an environment secret.
 *
 * The resolver ignores the principal's scope for KEY SELECTION — the secret is
 * the machine's, not the caller's — but it still takes a `Principal` because
 * that is what `CredentialStore` hands it, and because a future per-scope
 * secret map would change that.
 *
 * Async because the derivation is: `KeyResolver` allows `Promise<string>`.
 */
export function keyFromEnvironmentSecret(opts: EnvironmentKeyOptions): KeyResolver {
  return function resolve(_principal: Principal): Promise<string> {
    return deriveEnvironmentKey(opts);
  };
}

/**
 * Pick the right resolver for a scope, so a caller does not have to know.
 *
 * `passphrase` is listed first and wins if provided: a caller that HAS a
 * passphrase must not silently fall back to the ambient env secret.
 */
export function resolverFor(
  scope: 'user' | 'machine',
  input: { passphrase?: KeyResolver; environment?: KeyResolver },
): KeyResolver {
  if (scope === 'user') {
    if (!input.passphrase) {
      throw new Error(
        '[credentials] a user-scoped credential needs a passphrase resolver; ' +
          'an environment secret is ambient and is not offered for user secrets.',
      );
    }
    return input.passphrase;
  }
  if (!input.environment) {
    throw new Error('[credentials] a machine-scoped credential needs an environment resolver');
  }
  return input.environment;
}

/** The tag carried by an env-backed resolver, so consumers can tell. */
export const ENV_KEY_SCOPE: EnvKeyScope = 'environment';